import { isAdmin } from '@/constants/roles';
import { cn } from '@/lib/utils';
import {
    Download,
    Pause,
    Play,
    RefreshCw,
    Volume2,
    VolumeX,
    X
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import { toast } from 'sonner';

import {
    useChatData,
    useChatMessages,
    useConfig
} from '@chainlit/react-client';

import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from '@/components/ui/tooltip';

import { useAuth } from '@/hooks/useAuth';

import { PodcastStatus, podcastState } from '@/state/podcast';

interface PodcastPlayerProps {
    className?: string;
}

const PodcastPlayer = ({ className }: PodcastPlayerProps) => {
    const { threadId, messages } = useChatMessages();
    const { loading: chatLoading } = useChatData();
    const { config } = useConfig();
    const { user } = useAuth();
    const [podcast, setPodcast] = useRecoilState(podcastState);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [canGenerate, setCanGenerate] = useState(false);

    // Audio element managed via state for proper thread switching
    const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(
        null
    );
    // Version counter to force audio element remount after regeneration
    const [audioVersion, setAudioVersion] = useState(0);
    // Abort controller for cancelling generation
    const abortControllerRef = useRef<AbortController | null>(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [showSpeedSlider, setShowSpeedSlider] = useState(false);
    const [audioError, setAudioError] = useState<string | null>(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const speedControlRef = useRef<HTMLDivElement>(null);

    // Check if user is authorized to view/listen to podcasts
    // Only requires being in an allowed organization (admin check is for generation only)
    useEffect(() => {
        if (!user) {
            setIsAuthorized(false);
            setCanGenerate(false);
            return;
        }

        const userIsAdmin = isAdmin(user?.metadata?.organization_roles);
        const isEnabledForOrg = config?.features?.podcast_enabled !== false;

        setIsAuthorized(isEnabledForOrg);
        setCanGenerate(userIsAdmin && isEnabledForOrg);
    }, [user, config]);

    // The audio URL is the streaming endpoint - browser will handle streaming
    // Include created_at as cache buster to ensure fresh audio after regeneration
    const audioUrl =
        threadId && podcast.status?.has_podcast
            ? `/podcast/download/${threadId}${
                  podcast.status.created_at
                      ? `?t=${encodeURIComponent(podcast.status.created_at)}`
                      : ''
              }`
            : null;

    // Reset audio state when thread changes
    useEffect(() => {
        // Stop any playing audio and reset all local state
        if (audioElement) {
            audioElement.pause();
            audioElement.currentTime = 0;
        }
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
        setAudioError(null);
        setAudioElement(null); // Clear audio element so event listeners detach
    }, [threadId]);

    // Note: Podcast status is now fetched in the Chat component to prevent layout shift
    // This component just reads the podcast state from Recoil

    // Track message count to detect new messages after podcast generation
    const [messageCountAtGeneration, setMessageCountAtGeneration] = useState<
        number | null
    >(null);
    // Track the created_at timestamp to know when to reset message count
    const [lastPodcastCreatedAt, setLastPodcastCreatedAt] = useState<
        string | null
    >(null);

    // Update message count when podcast is generated or loaded (based on created_at changing)
    useEffect(() => {
        const createdAt = podcast.status?.created_at;
        if (
            podcast.status?.has_podcast &&
            createdAt &&
            createdAt !== lastPodcastCreatedAt
        ) {
            // Podcast was just generated/regenerated or loaded fresh - update tracking
            setLastPodcastCreatedAt(createdAt);
            setMessageCountAtGeneration(messages.length);
        }
    }, [podcast.status, messages.length, lastPodcastCreatedAt]);

    // Detect when new messages arrive after podcast was generated
    // Only mark as outdated if the message count increased AND we haven't just regenerated
    useEffect(() => {
        const createdAt = podcast.status?.created_at;
        // Don't mark outdated if created_at just changed (regeneration in progress)
        if (createdAt !== lastPodcastCreatedAt) return;

        if (
            podcast.status?.has_podcast &&
            !podcast.status.is_outdated &&
            messageCountAtGeneration !== null &&
            messages.length > messageCountAtGeneration
        ) {
            // Mark podcast as outdated
            setPodcast((prev) => ({
                ...prev,
                status: prev.status
                    ? { ...prev.status, is_outdated: true }
                    : null
            }));
        }
    }, [
        messages.length,
        messageCountAtGeneration,
        podcast.status,
        setPodcast,
        lastPodcastCreatedAt
    ]);

    // Reset message count tracking when thread changes
    useEffect(() => {
        setMessageCountAtGeneration(null);
        setLastPodcastCreatedAt(null);
    }, [threadId]);

    // Cleanup abort controller on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // Handle cancel generation
    const handleCancelGeneration = useCallback(async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }

        // Also notify backend to cancel (fire and forget)
        if (threadId) {
            fetch(`/podcast/cancel/${threadId}`, {
                method: 'POST',
                credentials: 'include'
            }).catch(() => {
                // Ignore errors - best effort cancellation
            });
        }

        setPodcast((prev) => ({ ...prev, isGenerating: false, error: null }));
    }, [setPodcast, threadId]);

    // Handle generate/update podcast
    const handleGeneratePodcast = useCallback(async () => {
        if (!threadId) return;

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();

        setPodcast((prev) => ({ ...prev, isGenerating: true, error: null }));

        // Don't stop current playback - let user continue listening while regenerating

        try {
            const response = await fetch('/podcast/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ thread_id: threadId }),
                signal: abortControllerRef.current.signal
            });

            if (!response.ok) {
                const errorData = await response
                    .json()
                    .catch(() => ({ detail: 'Unknown error' }));
                throw new Error(
                    errorData.detail || 'Failed to generate podcast'
                );
            }

            const result = await response.json();

            // Update state with new podcast info
            setPodcast((prev) => ({
                ...prev,
                isGenerating: false,
                status: {
                    has_podcast: true,
                    is_outdated: false,
                    podcast_url: result.podcast_url,
                    created_at: result.created_at,
                    last_message_at: result.last_message_at,
                    current_last_message_at: result.last_message_at,
                    size_bytes: result.size_bytes,
                    file_extension: result.file_extension || 'mp3',
                    podcast_name: result.podcast_name
                },
                error: null
            }));

            toast.success('Podcast generated successfully!');

            // Now stop old playback and reset for new podcast
            if (audioElement) {
                audioElement.pause();
            }
            setCurrentTime(0);
            setDuration(0);
            setIsPlaying(false);

            // Force remount of audio element by incrementing version
            setAudioVersion((v) => v + 1);

            // Note: messageCountAtGeneration will be updated by useEffect when created_at changes
        } catch (error: any) {
            // Don't show error if request was cancelled
            if (error.name === 'AbortError') {
                return;
            }
            console.error('Error generating podcast:', error);
            setPodcast((prev) => ({
                ...prev,
                isGenerating: false,
                error: error.message || 'Failed to generate podcast'
            }));
            toast.error(error.message || 'Failed to generate podcast');
        } finally {
            abortControllerRef.current = null;
        }
    }, [threadId, setPodcast, audioElement]);

    // Handle download
    const handleDownload = useCallback(async () => {
        if (!threadId || !podcast.status?.has_podcast || isDownloading) return;

        setIsDownloading(true);

        try {
            const response = await fetch(`/podcast/download/${threadId}`, {
                method: 'GET',
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Failed to download podcast');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Use podcast name for filename if available, otherwise fallback to thread ID
            const extension = podcast.status.file_extension || 'mp3';
            let filename: string;
            if (podcast.status.podcast_name) {
                // Make filename safe: replace spaces with underscores, remove special chars
                const safeName = podcast.status.podcast_name
                    .replace(/[^a-zA-Z0-9\s\-_]/g, '')
                    .replace(/\s+/g, '_')
                    .trim();
                filename = `${safeName}.${extension}`;
            } else {
                filename = `podcast_${threadId}.${extension}`;
            }

            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('Podcast downloaded!');
        } catch (error) {
            console.error('Error downloading podcast:', error);
            toast.error('Failed to download podcast');
        } finally {
            setIsDownloading(false);
        }
    }, [threadId, podcast.status, isDownloading]);

    // Audio controls
    const togglePlay = useCallback(async () => {
        if (!audioElement) return;

        try {
            if (isPlaying) {
                audioElement.pause();
            } else {
                await audioElement.play();
            }
        } catch (error: any) {
            // AbortError is expected when play() is interrupted by pause() - ignore it
            if (error.name === 'AbortError') {
                return;
            }
            console.error('Error toggling play:', error);
            toast.error('Failed to play audio');
        }
    }, [isPlaying, audioElement]);

    const toggleMute = useCallback(() => {
        if (!audioElement) return;

        const newMuted = !isMuted;
        audioElement.muted = newMuted;
        setIsMuted(newMuted);
    }, [isMuted, audioElement]);

    const handleVolumeChange = useCallback(
        (value: number[]) => {
            if (!audioElement) return;

            const newVolume = value[0];
            audioElement.volume = newVolume;
            setVolume(newVolume);

            if (newVolume === 0) {
                setIsMuted(true);
                audioElement.muted = true;
            } else if (isMuted) {
                setIsMuted(false);
                audioElement.muted = false;
            }
        },
        [isMuted, audioElement]
    );

    const handleSeek = useCallback(
        (value: number[]) => {
            if (!audioElement) return;
            audioElement.currentTime = value[0];
            setCurrentTime(value[0]);
        },
        [audioElement]
    );

    // Handle playback speed change from slider
    // Snaps to 0.1 steps below 1.0, and 0.25 steps at 1.0 and above
    const handleSpeedChange = useCallback(
        (value: number[]) => {
            if (!audioElement) return;
            let newRate = value[0];

            // Snap to appropriate step values
            if (newRate < 1) {
                // Below 1: snap to 0.1 increments (0.9)
                newRate = Math.round(newRate * 10) / 10;
                newRate = Math.max(0.9, newRate); // Minimum 0.9
            } else {
                // 1.0 and above: snap to 0.25 increments
                newRate = Math.round(newRate * 4) / 4;
                newRate = Math.min(2.5, newRate); // Maximum 2.5
            }

            audioElement.playbackRate = newRate;
            setPlaybackRate(newRate);
        },
        [audioElement]
    );

    // Audio event handlers - uses audioElement state instead of ref to properly handle remounting
    useEffect(() => {
        const audio = audioElement;
        if (!audio) return;

        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
        const handleLoadedMetadata = () => {
            console.log('Audio loaded, duration:', audio.duration);
            setDuration(audio.duration);
        };
        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
        };
        const handleCanPlay = () => {
            console.log('Audio can play, duration:', audio.duration);
            if (audio.duration && !isNaN(audio.duration)) {
                setDuration(audio.duration);
            }
        };
        const handleDurationChange = () => {
            console.log('Duration changed:', audio.duration);
            if (
                audio.duration &&
                !isNaN(audio.duration) &&
                isFinite(audio.duration)
            ) {
                setDuration(audio.duration);
            }
        };

        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('canplay', handleCanPlay);
        audio.addEventListener('durationchange', handleDurationChange);

        // Try to get duration immediately if already loaded
        if (
            audio.duration &&
            !isNaN(audio.duration) &&
            isFinite(audio.duration)
        ) {
            setDuration(audio.duration);
        }

        return () => {
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('canplay', handleCanPlay);
            audio.removeEventListener('durationchange', handleDurationChange);
        };
    }, [audioElement]);

    // Format time helper
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Don't render if no thread, loading, or no podcast
    if (!threadId || podcast.isLoading) {
        return null;
    }

    // Don't render if user is not authorized to use podcast feature
    if (!isAuthorized) {
        return null;
    }

    // Don't render if no podcast and user not authorized (403 response sets status to null)
    if (!podcast.status) {
        return null;
    }

    // If no podcast exists, don't show the player (user should use header button to generate)
    if (!podcast.status.has_podcast) {
        return null;
    }

    return (
        <div
            className={cn(
                'w-full mx-auto p-3 rounded-lg border bg-card shadow-xs',
                className
            )}
        >
            {/* Header with podcast label, title and regenerate button */}
            <div className="mb-2 pb-2 border-b flex items-center gap-2">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Podcast
                        </span>
                        {podcast.status.is_outdated && (
                            <span className="text-xs text-amber-600 font-medium">
                                (outdated)
                            </span>
                        )}
                    </div>
                    {podcast.status.podcast_name && (
                        <h4
                            className="text-sm font-medium truncate"
                            title={podcast.status.podcast_name}
                        >
                            🎙️ {podcast.status.podcast_name}
                        </h4>
                    )}
                </div>

                {/* Regenerate/Cancel button in header - only visible for admins */}
                {canGenerate && (
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant={
                                        podcast.isGenerating
                                            ? 'ghost-destructive'
                                            : 'ghost'
                                    }
                                    size="compact"
                                    className="h-8 px-2 gap-1.5 shrink-0"
                                    onClick={
                                        podcast.isGenerating
                                            ? handleCancelGeneration
                                            : handleGeneratePodcast
                                    }
                                    disabled={
                                        chatLoading && !podcast.isGenerating
                                    }
                                >
                                    {podcast.isGenerating ? (
                                        <>
                                            <RefreshCw className="animate-spin" />
                                            <span className="text-xs">
                                                Generating...
                                            </span>
                                            <X className="opacity-60" />
                                        </>
                                    ) : (
                                        <>
                                            <RefreshCw
                                                className={cn(
                                                    podcast.status
                                                        .is_outdated &&
                                                        'text-amber-600'
                                                )}
                                            />
                                            {podcast.status.is_outdated && (
                                                <span className="text-xs text-amber-600">
                                                    Update
                                                </span>
                                            )}
                                        </>
                                    )}
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent
                                side="top"
                                className="max-w-[200px]"
                            >
                                <p className="text-xs">
                                    {podcast.isGenerating
                                        ? 'Click to cancel generation'
                                        : chatLoading
                                        ? 'Wait for response to complete'
                                        : podcast.status.is_outdated
                                        ? 'Podcast outdated - new messages added. Click to regenerate.'
                                        : 'Regenerate podcast'}
                                </p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}
            </div>

            {/* Audio element - streams directly from backend endpoint */}
            {/* Key forces remount when thread, version, or created_at changes to ensure fresh audio state */}
            {audioUrl && (
                <audio
                    key={`${audioUrl}-${audioVersion}-${
                        podcast.status?.created_at || ''
                    }`}
                    ref={(el) => {
                        setAudioElement(el);
                    }}
                    src={audioUrl}
                    preload="auto"
                    onError={(e) => {
                        console.error('Audio error:', e);
                        setAudioError('Failed to play audio');
                    }}
                />
            )}

            <div className="flex items-center gap-3">
                {/* Play/Pause button */}
                <Button
                    pill
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10"
                    onClick={togglePlay}
                    disabled={!audioUrl}
                >
                    {isPlaying ? (
                        <Pause />
                    ) : (
                        <Play className="ml-0.5" />
                    )}
                </Button>

                {/* Progress and time */}
                <div className="flex-1 flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-10 text-right">
                        {formatTime(currentTime)}
                    </span>
                    <Slider
                        value={[currentTime]}
                        max={duration || 100}
                        step={1}
                        onValueChange={handleSeek}
                        className="flex-1"
                        disabled={!audioUrl}
                    />
                    <span className="text-xs text-muted-foreground w-10">
                        {formatTime(duration)}
                    </span>
                </div>

                {/* Volume control */}
                <div className="flex items-center gap-1">
                    <Button
                        variant="ghost"
                        size="icon-sm"
                       
                        onClick={toggleMute}
                    >
                        {isMuted || volume === 0 ? (
                            <VolumeX />
                        ) : (
                            <Volume2 />
                        )}
                    </Button>
                    <Slider
                        value={[isMuted ? 0 : volume]}
                        max={1}
                        step={0.1}
                        onValueChange={handleVolumeChange}
                        className="w-16"
                    />
                </div>

                {/* Playback speed control - hover to expand */}
                <div
                    ref={speedControlRef}
                    className="relative flex items-center"
                    onMouseEnter={() => setShowSpeedSlider(true)}
                    onMouseLeave={() => setShowSpeedSlider(false)}
                >
                    <Button
                        variant="ghost"
                        size="compact"
                        className="h-8 px-2 text-xs font-medium min-w-12"
                        title="Playback speed"
                    >
                        {playbackRate}x
                    </Button>

                    {/* Speed slider popup - pb-2 creates overlap with button for continuous hover */}
                    <div
                        className={cn(
                            'absolute bottom-full left-1/2 -translate-x-1/2 pb-2 transition-all duration-200',
                            showSpeedSlider
                                ? 'opacity-100 visible'
                                : 'opacity-0 invisible pointer-events-none'
                        )}
                    >
                        <div className="px-5 py-4 bg-popover border rounded-lg shadow-lg select-none">
                            <div className="flex flex-col items-center gap-4 w-52">
                                <div className="flex items-center justify-between w-full">
                                    <span className="text-sm text-muted-foreground">
                                        Speed: {playbackRate}x
                                    </span>
                                    {playbackRate !== 1 && (
                                        <Button
                                            variant="ghost"
                                            size="compact"
                                            onClick={() =>
                                                handleSpeedChange([1])
                                            }
                                            className="underline"
                                        >
                                            Reset
                                        </Button>
                                    )}
                                </div>
                                <Slider
                                    value={[playbackRate]}
                                    min={0.9}
                                    max={2.5}
                                    step={0.05}
                                    onValueChange={handleSpeedChange}
                                    className="w-full h-2"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Download button */}
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                               
                                onClick={handleDownload}
                                disabled={isDownloading}
                            >
                                {isDownloading ? (
                                    <RefreshCw className="animate-spin" />
                                ) : (
                                    <Download />
                                )}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                            <p className="text-xs">
                                {isDownloading
                                    ? 'Downloading...'
                                    : 'Download podcast'}
                            </p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
        </div>
    );
};

export default PodcastPlayer;
